import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  CircularProgress,
  Alert,
  Tooltip as MuiTooltip,
  IconButton
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Speed,
  Info
} from '@mui/icons-material';
import { authGet } from '../../services/apiService';

/**
 * PRClosureTimeChart component for displaying PR closure time distribution
 * @returns {JSX.Element} PRClosureTimeChart component
 */
const PRClosureTimeChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Color palette for different time buckets
  const COLORS = {
    '≤30 mins': '#4caf50',    // Green - Very fast
    '≤1 hr': '#8bc34a',      // Light green - Fast
    '≤2 hrs': '#ffc107',     // Yellow - Moderate
    '≤8 hrs': '#ff9800',     // Orange - Slow
    '>8 hrs': '#f44336'      // Red - Very slow
  };

  // Fetch PR closure time data on component mount
  useEffect(() => {
    const fetchPRClosureTimeData = async () => {
      try {
        setLoading(true);
        
        // Call the API endpoint to get PR closure time distribution
        const response = await authGet('/api/pull-requests/closure-time-distribution');
        
        if (response.success) {
          setData(response.data || {});
          setError(null);
          console.log('Fetched PR closure time data:', response.data);
        } else {
          throw new Error(response.message || 'Failed to load PR closure time data');
        }
      } catch (err) {
        console.error('Error fetching PR closure time data:', err);
        setError('Failed to load PR closure time data. Please try again later.');
        setData({});
      } finally {
        setLoading(false);
      }
    };
    
    fetchPRClosureTimeData();
  }, []);

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{ 
          bgcolor: 'background.paper', 
          border: '1px solid #ccc', 
          borderRadius: 1, 
          p: 1,
          boxShadow: 2
        }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {`Time Bucket: ${label}`}
          </Typography>
          <Typography variant="body2" color="primary">
            {`Count: ${payload[0].value} PRs`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {`Percentage: ${payload[0].payload.percentage}%`}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Custom label for pie chart
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data.distribution || data.distribution.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No PR closure time data available.
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center">
            <Speed sx={{ mr: 1, color: '#2196f3' }} />
            <Typography variant="h6" component="h3">
              PR Closure Time Distribution
            </Typography>
            <MuiTooltip title="Shows how many PRs are closed within different time buckets">
              <IconButton size="small">
                <Info fontSize="small" />
              </IconButton>
            </MuiTooltip>
          </Box>
        }
        subheader={
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" color="text.secondary">
              Based on {data.totalPRs} closed PRs
            </Typography>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
              Avg: {data.averageClosureTimeMinutes} minutes
            </Typography>
          </Box>
        }
      />
      <CardContent>
        <Box height={400}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.distribution} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="timeBucket" 
                fontSize={12}
                stroke="#666"
              />
              <YAxis 
                label={{ value: 'Number of PRs', angle: -90, position: 'insideLeft' }}
                fontSize={12}
                stroke="#666"
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend />
              
              {/* Bar chart for PR counts */}
              <Bar 
                dataKey="count" 
                name="Number of PRs"
                radius={[4, 4, 0, 0]}
              >
                {data.distribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[entry.timeBucket]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
        
        {/* Summary statistics */}
        <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="subtitle2" gutterBottom>
            Summary Statistics
          </Typography>
          <Box display="flex" gap={4} flexWrap="wrap">
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total PRs Analyzed
              </Typography>
              <Typography variant="h6" color="primary">
                {data.totalPRs}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Average Closure Time
              </Typography>
              <Typography variant="h6" color="primary">
                {data.averageClosureTimeMinutes} minutes
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Fastest Closure Rate
              </Typography>
              <Typography variant="h6" color="success.main">
                {data.distribution.reduce((max, item) => item.percentage > max.percentage ? item : max, data.distribution[0])?.timeBucket || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PRClosureTimeChart;
